import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PlanGate from '../components/PlanGate';
import { useOrcamento } from '../context/OrcamentoContext';
import usePlan from '../hooks/usePlan';
import { TIPO_LABELS } from '../data/cubData';
import estadosBrasil from '../data/estadosBrasil';
import LogService, { LOG_ACTIONS } from '../services/LogService';

function formatBRL(value) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function OrcamentoResultado() {
  const navigate = useNavigate();
  const { resultado, clearOrcamento } = useOrcamento();
  const { plan } = usePlan();

  useEffect(() => {
    if (!resultado) navigate('/orcamento/novo', { replace: true });
  }, [resultado, navigate]);

  if (!resultado) return null;

  const {
    tipo, padrao, estado, cubTipo, cubValor, areaTotal,
    valorBase, valorTotal, valorM2, etapas,
    itensEspeciais, somaItensEspeciais,
  } = resultado;

  const estadoNome = estadosBrasil.find((e) => e.sigla === estado)?.nome || estado;
  const tipoLabel = TIPO_LABELS[cubTipo] || cubTipo;

  const handleExportExcel = () => {
    const rows = [
      ['Orçamento por Planta - Quanto Custa Obra'],
      [],
      ['Estado', `${estadoNome} (${estado})`],
      ['Tipo', `${cubTipo} - ${tipoLabel}`],
      ['Area Total', `${areaTotal} m2`],
      ['CUB Base', `${formatBRL(cubValor)}/m2`],
      [],
      ['ETAPAS DA OBRA', '', ''],
      ['Etapa', '%', 'Valor (R$)'],
      ...etapas.map((e) => [e.nome, `${e.percentual}%`, e.valor.toFixed(2)]),
      ['Subtotal Construção', '100%', valorBase.toFixed(2)],
      [],
    ];

    if (itensEspeciais.length > 0) {
      rows.push(['ITENS ESPECIAIS', '', '']);
      rows.push(['Item', '', 'Valor (R$)']);
      itensEspeciais.forEach((i) => rows.push([i.nome, '', i.valor.toFixed(2)]));
      rows.push(['Subtotal Itens Especiais', '', somaItensEspeciais.toFixed(2)]);
      rows.push([]);
    }

    rows.push(['TOTAL ESTIMADO', '', valorTotal.toFixed(2)]);
    rows.push(['Custo por m2', '', valorM2.toFixed(2)]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 35 }, { wch: 15 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orçamento');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `QuantoCustaObra_Orcamento_${new Date().toISOString().slice(0, 10)}.xlsx`);

    LogService.log(LOG_ACTIONS.ORCAMENTO_EXPORT, {
      details: { tipo, padrao, estado, areaTotal, valorTotal },
    });
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = 15;

    // Header bar
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('QuantoCustaObra', margin, 16);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('quantocustaobra.com.br', margin, 23);
    doc.text(new Date().toLocaleDateString('pt-BR'), pageWidth - margin, 16, { align: 'right' });
    doc.text('Orçamento por Planta', pageWidth - margin, 23, { align: 'right' });
    y = 45;

    // Title
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Orçamento de Obra — Estimativa por Planta', margin, y);
    y += 12;

    // Project info box
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 24, 3, 3, 'FD');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    const col2 = pageWidth / 2 + 5;
    doc.text(`Tipo: ${tipoLabel}`, margin + 5, y + 8);
    doc.text(`Estado: ${estadoNome} (${estado})`, margin + 5, y + 16);
    doc.text(`Area Total: ${areaTotal} m2`, col2, y + 8);
    doc.text(`CUB Base: ${formatBRL(cubValor)}/m2`, col2, y + 16);
    y += 32;

    // Etapas table
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Etapas da Obra', margin, y);
    y += 4;

    const etapasRows = etapas.map((e) => [e.nome, `${e.percentual}%`, formatBRL(e.valor)]);
    etapasRows.push([
      { content: 'Subtotal Construção', styles: { fontStyle: 'bold' } },
      { content: '100%', styles: { fontStyle: 'bold', halign: 'center' } },
      { content: formatBRL(valorBase), styles: { fontStyle: 'bold', halign: 'right' } },
    ]);

    doc.autoTable({
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Etapa', '%', 'Valor (R$)']],
      body: etapasRows,
      theme: 'striped',
      headStyles: { fillColor: [15, 23, 42], fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8 },
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Itens Especiais table
    if (itensEspeciais.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Itens Especiais', margin, y);
      y += 4;

      const itensRows = itensEspeciais.map((i) => [i.nome, formatBRL(i.valor)]);
      itensRows.push([
        { content: 'Subtotal Itens Especiais', styles: { fontStyle: 'bold' } },
        { content: formatBRL(somaItensEspeciais), styles: { fontStyle: 'bold', halign: 'right' } },
      ]);

      doc.autoTable({
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Item', 'Valor (R$)']],
        body: itensRows,
        theme: 'striped',
        headStyles: { fillColor: [71, 85, 105], fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 1: { halign: 'right' } },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // Total highlight box
    doc.setFillColor(249, 115, 22);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 20, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('CUSTO TOTAL ESTIMADO', margin + 8, y + 9);
    doc.setFontSize(14);
    doc.text(formatBRL(valorTotal), pageWidth - margin - 8, y + 9, { align: 'right' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`${formatBRL(valorM2)}/m2`, pageWidth - margin - 8, y + 16, { align: 'right' });
    y += 28;

    // Disclaimer
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(253, 230, 138);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 14, 2, 2, 'FD');
    doc.setFontSize(7);
    doc.setTextColor(146, 64, 14);
    doc.setFont('helvetica', 'italic');
    doc.text('Este orçamento é uma estimativa baseada no CUB. Consulte um profissional para orçamento detalhado.', margin + 5, y + 9);

    // Footer
    const footerY = doc.internal.pageSize.getHeight() - 15;
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.setFont('helvetica', 'normal');
    doc.text('Gerado por QuantoCustaObra.com.br — Estimativa baseada no CUB', margin, footerY);
    doc.text(`CUB ${cubTipo} - ${estadoNome} | SINDUSCON`, pageWidth - margin, footerY, { align: 'right' });

    doc.save(`QuantoCustaObra_Orcamento_${new Date().toISOString().slice(0, 10)}.pdf`);

    LogService.log(LOG_ACTIONS.ORCAMENTO_EXPORT, {
      details: { tipo, padrao, estado, areaTotal, valorTotal, format: 'pdf' },
    });
  };

  const handleNovaAnalise = () => {
    clearOrcamento();
    navigate('/orcamento/novo');
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <Header />
      <main className="max-w-4xl mx-auto px-4 py-6 md:py-10 pt-36">
        {/* Bruno celebrating */}
        <div className="flex justify-center mb-6">
          <img src="/images/brand/bruno-calculadora.png" alt="Bruno" className="w-24 h-24 md:w-28 md:h-28 object-contain" />
        </div>

        {/* Main Card */}
        <div className="bg-card rounded-2xl shadow-card overflow-hidden mb-6">
          {/* Hero */}
          <div className="bg-gradient-hero p-6 md:p-8 text-center">
            <p className="text-white/70 text-sm font-medium mb-1">CUSTO TOTAL ESTIMADO</p>
            <p className="text-3xl md:text-4xl font-extrabold text-white font-display">{formatBRL(valorTotal)}</p>
            <div className="flex items-center justify-center gap-3 mt-3 text-white/60 text-xs">
              <span>{tipoLabel}</span>
              <span>|</span>
              <span>{areaTotal} m2</span>
              <span>|</span>
              <span>{estadoNome}</span>
              <span>|</span>
              <span>{formatBRL(valorM2)}/m2</span>
            </div>
          </div>

          {/* Etapas Table */}
          <div className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-secondary font-display">Etapas da Obra</h2>
              <span className="text-xs text-muted-foreground">CUB {cubTipo}: {formatBRL(cubValor)}/m2</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Etapa</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase w-16">%</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {(plan.isFreePlan ? etapas.slice(0, 2) : etapas).map((e) => (
                    <tr key={e.id} className="border-b border-border/30 hover:bg-muted">
                      <td className="px-3 py-2.5 text-foreground">{e.nome}</td>
                      <td className="px-3 py-2.5 text-center text-muted-foreground">{e.percentual}%</td>
                      <td className="px-3 py-2.5 text-right font-medium text-foreground">{formatBRL(e.valor)}</td>
                    </tr>
                  ))}
                  {!plan.isFreePlan && (
                    <tr className="bg-muted border-t border-border">
                      <td className="px-3 py-3 font-bold text-secondary">Subtotal Construção</td>
                      <td className="px-3 py-3 text-center font-bold text-muted-foreground">100%</td>
                      <td className="px-3 py-3 text-right font-bold text-secondary">{formatBRL(valorBase)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Freemium Block */}
          {plan.isFreePlan && (
            <div className="px-6 md:px-8 pb-6 md:pb-8">
              <div className="bg-primary/5 border-2 border-primary/20 rounded-2xl p-6 text-center">
                <img src="/images/brand/bruno-principal.png" alt="Bruno" className="w-16 h-16 mx-auto mb-3 object-contain" />
                <p className="text-foreground font-medium mb-2">
                  Sua obra tem {etapas.length} etapas e vai custar aproximadamente <strong className="text-primary">{formatBRL(valorTotal)}</strong>.
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  Para ver o detalhamento completo de todas as etapas:
                </p>
                <Link to="/checkout/basico" className="no-underline">
                  <button className="w-full sm:w-auto px-8 py-3 bg-gradient-cta text-white font-bold rounded-xl shadow-cta hover:opacity-90 transition-all text-sm">
                    Ver orçamento completo — R$ 49,90/mês
                  </button>
                </Link>
                <div className="mt-3">
                  <Link to="/planos" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Ou assine um plano →
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Itens Especiais */}
          {itensEspeciais.length > 0 && !plan.isFreePlan && (
            <div className="px-6 md:px-8 pb-6 md:pb-8">
              <h2 className="text-lg font-bold text-secondary mb-4 font-display">Itens Especiais</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Item</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itensEspeciais.map((i) => (
                      <tr key={i.id} className="border-b border-border/30 hover:bg-muted">
                        <td className="px-3 py-2.5 text-foreground">{i.nome}</td>
                        <td className="px-3 py-2.5 text-right font-medium text-foreground">{formatBRL(i.valor)}</td>
                      </tr>
                    ))}
                    <tr className="bg-muted border-t border-border">
                      <td className="px-3 py-3 font-bold text-secondary">Subtotal Itens Especiais</td>
                      <td className="px-3 py-3 text-right font-bold text-secondary">{formatBRL(somaItensEspeciais)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-border bg-muted px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-muted-foreground">
              <p>CUB {cubTipo} - {estadoNome} | Ref: SINDUSCON</p>
              <p className="italic">* Estimativa baseada em CUB. Não substitui orçamento detalhado.</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mb-6">
          <PlanGate minPlan="basico" inline>
            <button
              onClick={handleExportPDF}
              className="flex-1 py-3 text-sm font-bold text-white bg-red-600 rounded-xl shadow-sm hover:bg-red-700 cursor-pointer transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Baixar PDF
            </button>
          </PlanGate>
          <PlanGate minPlan="basico" inline>
            <button
              onClick={handleExportExcel}
              className="flex-1 py-3 text-sm font-bold text-white bg-green-600 rounded-xl shadow-sm hover:bg-green-700 cursor-pointer transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Baixar Excel
            </button>
          </PlanGate>
          <button
            onClick={handleNovaAnalise}
            className="flex-1 py-3 text-sm font-bold text-foreground bg-card border border-border rounded-xl shadow-sm hover:bg-muted cursor-pointer transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Nova Análise
          </button>
        </div>

        {/* Upsell Card with Bruno handshake */}
        <div className="bg-secondary rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <img src="/images/brand/bruno-handshake.png" alt="Bruno" className="w-16 h-16 md:w-20 md:h-20 object-contain flex-shrink-0" />
            <div>
              <h3 className="text-sm font-bold text-secondary-foreground mb-1 font-display">Quer um orçamento DETALHADO?</h3>
              <p className="text-xs text-secondary-foreground/70 mb-3">
                Use nosso orçamento SINAPI com composições de custo oficiais, insumos detalhados e preços regionalizados.
              </p>
              <Link
                to="/orcamento-sinapi"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-brand-orange rounded-lg hover:bg-brand-orange-dark transition-colors no-underline"
              >
                Orçamento SINAPI
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default OrcamentoResultado;
